// src/lib/roomEdit.js
//
// What the Studio room editor sends (mission 004 Stage 1b, Astra 07 item 1).
// Plain ESM, no React, so it runs under Deno too.
//
// A room is either a CREDITS room (payment_origin 'credits', created by
// credits_create_room: priced in USD cents) or a LEGACY room (every room from
// before, sold on chain or free). A legacy room can never take a USD price,
// so an edit of one never carries ticketPriceCents or an access change, and
// sends total seats only when the creator changed them (a chain-registered
// room refuses a seat change).

/** can this editor price the room in USD? a new room or a credits room only */
export function roomPricingEditable(editRoom) {
  return !editRoom || editRoom.paymentOrigin === 'credits';
}

/**
 * The editor's submission. `form`: { title, description, thumbnail, duration,
 * accessTier, cents (null = free), totalSeats, scheduledAt, scheduledTime,
 * replayPriceCents (a new room only: cents, 0 = free, null = not for sale) }.
 * A price is sent only when it changed: the server refuses a price change once
 * a room has started, which would otherwise block an ordinary title edit.
 */
export function roomEditPayload(editRoom, form) {
  const out = {
    id: editRoom?.id,
    title: form.title,
    description: form.description,
    thumbnail: form.thumbnail,
    duration: form.duration,
    scheduledAt: form.scheduledAt,
    scheduledTime: form.scheduledTime,
  };
  if (!roomPricingEditable(editRoom)) {
    if (editRoom && Number(form.totalSeats) !== Number(editRoom.totalSeats)) out.totalSeats = form.totalSeats;
    return out;
  }
  out.accessTier = form.accessTier;
  out.totalSeats = form.totalSeats;
  const cents = form.accessTier === 'free' ? null : form.cents;
  if (!editRoom || (cents ?? null) !== (editRoom.ticketPriceCents ?? null)) out.ticketPriceCents = cents;
  // a NEW room's replay price (012-01): cents (0 = free) or null (not for sale);
  // an existing room's replay is priced through room-commerce set_price
  if (!editRoom && form.replayPriceCents !== undefined) out.replayPriceCents = form.replayPriceCents;
  return out;
}

/** the editor's / any caller's fields -> the update-room / create-room body */
export function normalizeRoomUpdates(data) {
  const out = {};

  if (data.title !== undefined) out.title = data.title;
  if (data.description !== undefined) out.description = data.description;
  if (data.thumbnail !== undefined) out.thumbnail = data.thumbnail;
  if (data.scheduledAt !== undefined) out.scheduledAt = data.scheduledAt;
  if (data.duration !== undefined) out.durationMinutes = data.duration;
  if (data.accessMode !== undefined) out.accessMode = data.accessMode;
  // minXPassRequired: no longer forwarded — access tiers died with the
  // curve (founding cards are binary); update-room rejects the field.
  if (data.totalSeats !== undefined) out.totalSeats = data.totalSeats;

  if (out.accessMode === undefined && data.accessTier !== undefined) {
    if (data.accessTier === 'free') out.accessMode = 'open';
    else if (data.accessTier === 'paid') out.accessMode = 'ticket_only';
  }

  // USD cents only: the server refuses any ETH (wei) price now
  if (data.ticketPriceCents !== undefined) {
    out.ticketPriceCents = data.ticketPriceCents == null || data.ticketPriceCents === 0 ? null : data.ticketPriceCents;
  }

  if (data.isInstant !== undefined) out.isInstant = data.isInstant;
  // a new room's replay price; null = not for sale (create-room applies it)
  if (data.replayPriceCents !== undefined) out.replayPriceCents = data.replayPriceCents;

  return out;
}
